import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { analytic } from '../utils/Analytics.js';
import {
    VOICE_CONNECTING,
    VOICE_HANGED_UP,
    VOICE_LISTENING,
    VOICE_PAUSED,
    VOICE_SPEAKING,
    VOICE_THINKING,
} from '../utils/voiceStatus.js';

const TOAST_ERROR_STYLE = {
    position: 'top-right',
    style: {
        backgroundColor: 'white',
        color: 'red',
        fontSize: '13px',
        padding: '5px 7px',
        fontWeight: '500',
    },
};

const useVoiceMode = ({
    sendJsonMessage,
    messages,
    setMessages,
    botId,
    languages,
    SAMPLE_RATE,
}) => {
    const { i18n, t } = useTranslation();

    const [voiceModeEnabled, setVoiceModeEnabled] = useState(false);
    const [voiceMode, setVoiceMode] = useState(false);
    const [isMicMuted, setIsMicMuted] = useState(true);
    const [isSpeakerMuted, setIsSpeakerMuted] = useState(true);
    const [isAudioInteractionsDisabled, setIsAudioInteractionsDisabled] = useState(true);
    const [audioLevel, setAudioLevel] = useState(0);
    const [audioQueue, setAudioQueue] = useState([]);
    const [voiceErr, setVoiceErr] = useState(false);
    const [hangedUp, setHangedUp] = useState(false);

    const voiceStatus = useRef(VOICE_CONNECTING);
    const audioStatusRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const playAudioRef = useRef(false);
    const audioContextRef = useRef(null);
    const gainNodeRef = useRef(null);
    const animationFrameRef = useRef(null);
    const isMicMutedRef = useRef(true);
    const isWelcomeMessageRequested = useRef(false);
    const currentAudioSourceRef = useRef(null);
    const audioThresholdRef = useRef(null);

    // ── Audio helpers ──

    const stereoToMono = (audioContext, stereoBuffer) => {
        const length = stereoBuffer.length;
        const sampleRate = stereoBuffer.sampleRate;

        const left = stereoBuffer.getChannelData(0);
        const right = stereoBuffer.numberOfChannels > 1
            ? stereoBuffer.getChannelData(1)
            : left;

        const monoData = new Float32Array(length);
        for (let i = 0; i < length; i++) {
            monoData[i] = (left[i] + right[i]) / 2;
        }

        const monoBuffer = audioContext.createBuffer(1, length, sampleRate);
        monoBuffer.copyToChannel(monoData, 0);
        return monoBuffer;
    };

    const handleCloseAudio = () => {
        if (audioStatusRef.current) {
            audioStatusRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        if (playAudioRef.current) {
            playAudioRef.current = false;
        }

        if (gainNodeRef.current) {
            gainNodeRef.current = null;
        }

        if (currentAudioSourceRef.current) {
            currentAudioSourceRef.current.disconnect();
            currentAudioSourceRef.current = null;
        }

        if (audioThresholdRef.current) {
            audioThresholdRef.current = null;
        }

        setAudioLevel(0);
        setAudioQueue([]);
    };

    // ── Recording ──

    const stopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current = null;
            audioStatusRef.current = 'stopped';
        }
    };

    const pauseRecording = () => {
        if ((voiceStatus.current == VOICE_LISTENING || voiceStatus.current == VOICE_THINKING || voiceStatus.current == VOICE_SPEAKING) && streamRef.current) {
            const audioTrack = streamRef.current?.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = false;
            }
        }
    };

    const resumeRecording = () => {
        if ((voiceStatus.current == VOICE_PAUSED || voiceStatus.current == VOICE_SPEAKING) && streamRef.current) {
            const audioTrack = streamRef.current?.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = true;
                if (!mediaRecorderRef.current && audioStatusRef.current === 'stopped') {
                    mediaRecorderRef.current = new MediaRecorder(streamRef.current);
                    mediaRecorderRef.current.start();
                    audioStatusRef.current = 'recording';
                }
            }
        }
    };

    // ── Interruption ──

    const handleInterruption = () => {
        if (currentAudioSourceRef.current) {
            try {
                currentAudioSourceRef.current.stop();
            } catch (err) {
                console.warn('Error stopping source:', err);
            }
            currentAudioSourceRef.current.disconnect();
            currentAudioSourceRef.current = null;
        }

        if (gainNodeRef.current) {
            gainNodeRef.current = null;
        }

        setAudioQueue([]);
        playAudioRef.current = false;

        voiceStatus.current = VOICE_LISTENING;

        sendJsonMessage({
            event: 'user_interruption',
            data: { value: 'false' },
        });
    };

    // ── Playback ──

    const playAudio = (buffer) => {
        if (hangedUp) return;

        const audioContext = audioContextRef.current;
        if (!audioContext) return;

        if (currentAudioSourceRef.current) {
            try {
                currentAudioSourceRef.current.stop();
            } catch (err) {
                console.warn('Error stopping previous source:', err);
            }
            currentAudioSourceRef.current.disconnect();
            currentAudioSourceRef.current = null;
        }

        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        currentAudioSourceRef.current = source;

        if (!gainNodeRef.current) {
            gainNodeRef.current = audioContext.createGain();
            gainNodeRef.current.connect(audioContext.destination);
        }

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        source.connect(gainNodeRef.current);
        gainNodeRef.current.connect(analyser);
        analyser.connect(audioContext.destination);

        playAudioRef.current = true;

        const updateAudioLevel = () => {
            analyser.getByteFrequencyData(dataArray);
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
                sum += dataArray[i];
            }
            const average = sum / dataArray.length;
            setAudioLevel(average);
            animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        };

        if (analyser && voiceStatus.current == VOICE_LISTENING) {
            updateAudioLevel();
        }

        source.onended = () => {
            playAudioRef.current = false;
            currentAudioSourceRef.current = null;

            if (audioQueue.length > 0) {
                playAudio(audioQueue.shift());
            } else {
                if (isWelcomeMessageRequested.current) {
                    isWelcomeMessageRequested.current = false;
                }

                if (voiceMode && !isMicMutedRef.current) {
                    voiceStatus.current = VOICE_LISTENING;

                    sendJsonMessage({ event: 'audio_unmute' });
                    sendJsonMessage({
                        event: 'user_interruption',
                        data: { value: 'false' },
                    });

                    if (streamRef.current) {
                        const audioTrack = streamRef.current.getAudioTracks()[0];
                        if (audioTrack) {
                            audioTrack.enabled = true;
                        }
                        audioStatusRef.current = 'recording';
                    }
                }
            }
        };

        source.start(0);
    };

    const decodeAndPlay = (arrayBuffer) => {
        const audioContext = audioContextRef.current;
        if (audioContext) {
            audioContext.decodeAudioData(arrayBuffer).then((buffer) => {
                const monoBuffer = buffer.numberOfChannels > 1 ? stereoToMono(audioContext, buffer) : buffer;
                if (playAudioRef.current) {
                    setAudioQueue((prevQueue) => [...prevQueue, monoBuffer]);
                } else {
                    playAudio(monoBuffer);
                }
            });
        }
    };

    // ── Audio analyser (sends mic data to server) ──

    const audioAnalyser = async () => {
        let stream = streamRef.current;
        if (!stream) {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: SAMPLE_RATE,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            streamRef.current = stream;
        }

        let audioContext = audioContextRef.current;
        if (!audioContext) {
            audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
            audioContextRef.current = audioContext;
        }

        try {
            await audioContext.audioWorklet.addModule('/audio/audio-processor.worklet.js');
        } catch (error) {
            console.error('Error loading audio worklet:', error);
            return;
        }

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;

        const source = audioContext.createMediaStreamSource(stream);
        const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');

        function bufferToBase64(buffer) {
            const bytes = new Uint8Array(buffer);
            let binary = '';
            for (let i = 0; i < bytes.byteLength; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            return btoa(binary);
        }

        let smoothedAudioLevel = 0;
        const SMOOTHING_FACTOR = 0.9;

        workletNode.port.onmessage = (event) => {
            if (event.data.event === 'emit_audio') {
                const language = languages.find((lng) => lng.value === i18n.language);
                const base64Audio = bufferToBase64(event.data.data);

                const audioData = {
                    event: event.data.event,
                    data: {
                        bot_id: botId,
                        language: language,
                        audio_data: base64Audio,
                    },
                };

                const currentLevel = event.data.audioLevel;
                smoothedAudioLevel = smoothedAudioLevel * SMOOTHING_FACTOR + currentLevel * (1 - SMOOTHING_FACTOR);

                if (smoothedAudioLevel > audioThresholdRef.current && voiceStatus.current == VOICE_SPEAKING) {
                    console.log("HIGHER AUDIO LEVELESLSLSLSL", smoothedAudioLevel, audioThresholdRef.current);
                    handleInterruption();
                }

                sendJsonMessage(audioData);
            }
        };

        source.connect(analyser);
        source.connect(workletNode);
        workletNode.connect(audioContext.destination);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const updateAudioLevel = () => {
            if (!analyser) return;

            if (voiceStatus.current == VOICE_LISTENING) {
                analyser.getByteFrequencyData(dataArray);
                let sum = 0;
                for (let i = 0; i < bufferLength; i++) {
                    sum += dataArray[i];
                }
                const average = sum / bufferLength;
                setAudioLevel(average);
            } else {
                setAudioLevel(0);
            }

            animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        };

        updateAudioLevel();
    };

    // ── Recording start ──

    const startRecording = async () => {
        try {
            let stream = streamRef.current;
            if (!stream) {
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        channelCount: 1,
                        sampleRate: SAMPLE_RATE,
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true,
                    },
                });
                streamRef.current = stream;
            }

            if (!mediaRecorderRef.current) {
                mediaRecorderRef.current = new MediaRecorder(stream);
            }

            if (messages.length == 1 && messages[0].role == 'assistant') {
                voiceStatus.current = VOICE_SPEAKING;
            } else {
                voiceStatus.current = VOICE_LISTENING;

                sendJsonMessage({
                    event: 'user_interruption',
                    data: { value: 'false' },
                });
            }

            audioStatusRef.current = 'recording';
            mediaRecorderRef.current.start();

            audioAnalyser();
        } catch (error) {
            console.error('Error accessing microphone:', error);
            setVoiceErr(true);
            setIsAudioInteractionsDisabled(true);
        }
    };

    // ── Calibration ──

    const startCallibration = async () => {
        let stream = streamRef.current;
        if (!stream) {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: SAMPLE_RATE,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            streamRef.current = stream;
        }

        let audioContext = audioContextRef.current;
        if (!audioContext) {
            audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
            audioContextRef.current = audioContext;
        }

        try {
            await audioContext.audioWorklet.addModule('/audio/audio-callibration.worklet.js');
        } catch (error) {
            console.error('Error loading audio worklet:', error);
            return;
        }

        const source = audioContext.createMediaStreamSource(stream);
        const workletNode = new AudioWorkletNode(audioContext, 'audio-callibration');

        const samples = [];

        workletNode.port.onmessage = (event) => {
            if (event.data.event === 'audio_threshold') {
                const rms = event.data.data;
                samples.push(rms);

                if (samples.length >= 200) {
                    const mean = samples.reduce((sum, val) => sum + val, 0) / samples.length;
                    let audioThreshold = Math.max(0.05, Math.min(mean * 2.5, 0.35));
                    audioThresholdRef.current = audioThreshold;
                    workletNode.disconnect();
                    source.disconnect();
                }
            }
        };

        source.connect(workletNode);
        workletNode.connect(audioContext.destination);
    };

    // ── Connection management ──

    const connectVoiceMode = () => {
        voiceStatus.current = VOICE_CONNECTING;

        const language = languages.find((lng) => lng.value === i18n.language);

        sendJsonMessage({
            event: 'open_audio',
            data: {
                bot_id: botId,
                language: language,
            },
        });

        startCallibration();
    };

    const disconnectVoiceMode = (status, sendMessage = true) => {
        setIsSpeakerMuted(true);
        setIsMicMuted(true);
        isMicMutedRef.current = true;
        setIsAudioInteractionsDisabled(true);
        setVoiceErr(false);

        voiceStatus.current = status || VOICE_CONNECTING;

        if (sendMessage) {
            sendJsonMessage({ event: 'close_audio' });
        }

        stopRecording();
        handleCloseAudio();
    };

    const playWelcomeMessage = () => {
        sendJsonMessage({ event: 'audio_mute' });
        sendJsonMessage({ event: 'play_welcome_message' });
        isWelcomeMessageRequested.current = true;
    };

    // ── Public API ──

    const openVoiceMode = () => {
        setAudioLevel(0);
        setVoiceMode(true);
        connectVoiceMode();
    };

    const exitVoiceMode = () => {
        setVoiceMode(false);
        setHangedUp(false);
        disconnectVoiceMode();
        analytic("voice_mode_exit", null);
    };

    const handleMicMuteToggle = () => {
        if (voiceStatus.current == VOICE_LISTENING || voiceStatus.current == VOICE_THINKING || voiceStatus.current == VOICE_SPEAKING) {
            sendJsonMessage({ event: 'audio_mute' });

            setIsMicMuted(true);
            isMicMutedRef.current = true;

            if (gainNodeRef.current) {
                gainNodeRef.current.gain.value = 0;
            }

            if (voiceStatus.current == VOICE_SPEAKING) {
                if (audioContextRef.current) {
                    // audioContextRef.current.suspend();
                }
            }

            voiceStatus.current = VOICE_PAUSED;
        } else if (voiceStatus.current == VOICE_PAUSED) {
            setIsMicMuted(false);
            isMicMutedRef.current = false;

            if (gainNodeRef.current) {
                gainNodeRef.current.gain.value = 1;
            }

            if (audioContextRef.current) {
                if (audioQueue.length === 0 && !playAudioRef.current) {
                    voiceStatus.current = VOICE_LISTENING;

                    sendJsonMessage({ event: 'audio_unmute' });
                    sendJsonMessage({
                        event: 'user_interruption',
                        data: { value: 'false' },
                    });
                } else {
                    voiceStatus.current = VOICE_SPEAKING;

                    sendJsonMessage({
                        event: 'user_interruption',
                        data: { value: 'true' },
                    });
                }
            }
        }
    };

    const hangupVoice = () => {
        disconnectVoiceMode(VOICE_HANGED_UP);
        setHangedUp(true);
    };

    const reOpenVoice = () => {
        setHangedUp(false);
        voiceStatus.current = VOICE_CONNECTING;
        connectVoiceMode();
        analytic("voice_mode_reconnect", null);
    };

    // ── Socket event handler ──
    // Returns true if the event was a voice event and was handled,
    // false if it should be handled by the caller (text message, etc.)

    const handleVoiceSocketEvent = (response) => {
        if (response.event == 'open_audio_success') {
            startRecording();

            if (messages.length == 1 && messages[0].role == 'assistant') {
                playWelcomeMessage();
            }

            setIsMicMuted(false);
            isMicMutedRef.current = false;
            setIsSpeakerMuted(false);
            setIsAudioInteractionsDisabled(false);

            analytic("voice_mode_initiated", null);
            return true;
        }

        if (response.event == 'play_welcome_message_error') {
            sendJsonMessage({ event: 'audio_unmute' });

            voiceStatus.current = VOICE_LISTENING;

            sendJsonMessage({
                event: 'user_interruption',
                data: { value: 'false' },
            });
            return true;
        }

        if (response.event == 'emit_stt') {
            if (response.type == 'complete') {
                voiceStatus.current = VOICE_THINKING;
                audioStatusRef.current = 'stopped';

                sendJsonMessage({ event: 'audio_mute' });

                setMessages((prevMessage) => [...prevMessage, {
                    role: 'user',
                    content: response.text,
                    is_flagged: 0,
                    reason: '',
                    messaged_at: Date.now(),
                }]);
            }
            return true;
        }

        if (response.event == 'play_audio') {
            if (voiceStatus.current == VOICE_THINKING) {
                setAudioQueue([]);
            }

            voiceStatus.current = VOICE_SPEAKING;

            if (!isWelcomeMessageRequested.current) {
                sendJsonMessage({ event: 'audio_unmute' });
            }

            sendJsonMessage({
                event: 'user_interruption',
                data: { value: 'true' },
            });

            const base64 = response.audio_data;
            const binaryString = atob(base64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            if (!audioContextRef.current) {
                audioContextRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
            }

            decodeAndPlay(bytes.buffer);
            return true;
        }

        if (response.event == 'asr_error' || response.event == 'open_audio_error' || response.event == 'emit_audio_error') {
            toast.error(response?.message || t('Something broke! Please try again later'), TOAST_ERROR_STYLE);
            exitVoiceMode();
            return true;
        }

        if (response.event == 'close_audio_success') {
            return true;
        }

        if (response.event == 'clear_audio') {
            return true;
        }

        return false;
    };

    return {
        voiceModeEnabled,
        setVoiceModeEnabled,
        voiceMode,
        voiceStatus: voiceStatus.current,
        isMicMuted,
        isSpeakerMuted,
        isAudioInteractionsDisabled,
        audioLevel,
        hangedUp,
        voiceErr,

        openVoiceMode,
        exitVoiceMode,
        handleMicMuteToggle,
        hangupVoice,
        reOpenVoice,
        handleVoiceSocketEvent,
    };
};

export default useVoiceMode;
